import type { Repositories, RestoredCounts, TableRows } from '@core/db';
import { SCHEMA_VERSION } from '@core/db';
import type { FileStore } from '@core/files';
import {
  DATABASE_PATH,
  FILES_PREFIX,
  FORMAT_VERSION,
  MANIFEST_PATH,
  archiveFileName,
  readManifest,
  type ArchiveContents,
  type ArchiveManifest,
  type ParsedArchive,
} from './format';
import { readZip, writeZip, type ZipEntry } from './zip';

/**
 * Архив дел: выгрузка на устройство и загрузка обратно.
 *
 * **Зачем он вообще есть.** В вебе иконку сносят с домашнего экрана одним долгим
 * нажатием, и приватное хранилище уходит вместе с ней — молча, без корзины и без копии
 * в iCloud. На Android экспорта нет намеренно ([D-25](docs/DECISIONS.md)), а здесь без
 * него материалы дела теряются по неосторожности. Поэтому архив входит в веб-MVP
 * обязательно ([D-47](docs/DECISIONS.md)).
 */

/** Собранный архив: сам файл, имя для системного листа и что в него попало. */
export interface BuiltArchive {
  blob: Blob;
  fileName: string;
  contents: ArchiveContents;
  /**
   * Документы, у которых не нашлось файла на диске.
   *
   * Такое бывает после сбоя записи, и молчать об этом нельзя: юрист вправе знать,
   * что архив полон **не** целиком, — иначе он узнает об этом при восстановлении.
   */
  missing: string[];
}

const encode = (value: unknown): Blob =>
  new Blob([JSON.stringify(value)], { type: 'application/json' });

/**
 * Собирает архив из базы и файлов.
 *
 * В архив идут только файлы живых документов: осиротевшие — мусор, который на этом же
 * экране предлагается убрать, и тащить его на другой телефон незачем.
 */
export async function buildArchive(
  db: Repositories,
  files: FileStore,
  appVersion: string,
  at: number = Date.now(),
): Promise<BuiltArchive> {
  const tables = await db.archive.dump();
  const live = await db.documents.liveFilesByCase();

  const entries: ZipEntry[] = [];
  const missing: string[] = [];

  for (const [caseId, fileNames] of live) {
    for (const fileName of fileNames) {
      const path = `cases/${caseId}/${fileName}`;
      const blob = await files.readPath(path);

      if (!blob) {
        missing.push(path);
        continue;
      }
      entries.push({ path: `${FILES_PREFIX}${path}`, blob });
    }
  }

  const contents: ArchiveContents = {
    cases: tables.cases?.length ?? 0,
    documents: tables.documents?.length ?? 0,
    clients: tables.clients?.length ?? 0,
    events: tables.events?.length ?? 0,
    files: entries.length,
  };

  const manifest: ArchiveManifest = {
    app: 'yatwa',
    kind: 'archive',
    formatVersion: FORMAT_VERSION,
    schemaVersion: SCHEMA_VERSION,
    createdAt: at,
    appVersion,
    contents,
  };

  /*
   * Описание — первой записью, содержимое базы — второй.
   *
   * Так архив можно понять, прочитав его начало: и наш разбор, и человек с обычным
   * архиватором видят, что это, до того как доберутся до сотен мегабайт документов.
   */
  const blob = await writeZip(
    [
      { path: MANIFEST_PATH, blob: encode(manifest) },
      { path: DATABASE_PATH, blob: encode({ tables }) },
      ...entries,
    ],
    at,
  );

  return { blob, fileName: archiveFileName(at), contents, missing };
}

/** Строки таблицы из чужого JSON: всё, что не объект, до базы не доходит. */
function rowsOf(value: unknown): TableRows {
  if (typeof value !== 'object' || value === null) return {};

  const tables: Record<string, unknown[]> = {};
  for (const [table, rows] of Object.entries(value)) {
    if (!Array.isArray(rows)) continue;
    tables[table] = rows.filter(
      (row) => typeof row === 'object' && row !== null && !Array.isArray(row),
    );
  }
  return tables as TableRows;
}

/**
 * Путь файла внутри архива → путь на устройстве.
 *
 * Пускаем только `cases/<дело>/<файл>` — ту самую раскладку, которую пишет хранилище.
 * Всё остальное отбрасывается: архив приходит файлом извне, и записывать по путям,
 * которые в нём написаны, без разбора нельзя.
 */
function devicePath(entry: string): string | null {
  if (!entry.startsWith(FILES_PREFIX)) return null;

  const path = entry.slice(FILES_PREFIX.length);
  const parts = path.split('/');

  if (parts.length !== 3 || parts[0] !== 'cases') return null;
  if (parts.some((part) => part.length === 0 || part === '.' || part === '..')) return null;

  return path;
}

/** Разбирает архив и проверяет, что он наш и что мы его понимаем. */
export async function readArchive(file: Blob): Promise<ParsedArchive> {
  const zip = await readZip(file);

  const manifestFile = zip.get(MANIFEST_PATH);
  if (!manifestFile) throw new Error('Это не архив Yatwa: внутри нет описания');

  const manifest = readManifest(JSON.parse(await manifestFile.text()));

  const databaseFile = zip.get(DATABASE_PATH);
  if (!databaseFile) throw new Error('Архив повреждён: в нём нет данных');

  const parsed: unknown = JSON.parse(await databaseFile.text());
  const tables = rowsOf(
    typeof parsed === 'object' && parsed !== null
      ? (parsed as { tables?: unknown }).tables
      : undefined,
  );

  const files = new Map<string, Blob>();
  for (const [entry, blob] of zip) {
    const path = devicePath(entry);
    if (path) files.set(path, blob);
  }

  return { manifest, tables, files };
}

/** Что архив принёс на устройство. */
export interface RestoreSummary extends RestoredCounts {
  files: number;
}

/**
 * Разворачивает разобранный архив на устройстве.
 *
 * **Файлы пишутся раньше записей.** Обратный порядок оставил бы после сбоя документ
 * в списке дела, который не открывается; этот — оставит файл без записи, а такой файл
 * уборка на экране «Хранилище» находит и предлагает удалить.
 */
export async function applyArchive(
  db: Repositories,
  files: FileStore,
  archive: ParsedArchive,
): Promise<RestoreSummary> {
  let written = 0;

  for (const [path, blob] of archive.files) {
    const result = await files.writePath(path, blob);
    if (result.ok) written += 1;
  }

  const counts = await db.archive.restore(archive.tables);
  return { ...counts, files: written };
}
